import { useCallback, useEffect, useState } from "react";
import { useAlert } from "context/AlertContext";
import { errorMessage } from "../features/builder.utils";

const INITIAL_QUERY = { page: 1, size: 10, searchText: "", search: {}, sort: { key: "sort_order", direction: "asc" } };

/**
 * List state of a Reminder Setup tab: search, advanced filter, server-side sort and paging. `fetchPage(payload)` is
 * the tab's POST get-all; rows come back with `__index` (their position on the page, for the S.No column).
 */
export default function useServerList(fetchPage, { loadError = "Failed to load the list.", initialSort = INITIAL_QUERY.sort } = {}) {
  const { showAlert } = useAlert();
  const initial = { ...INITIAL_QUERY, sort: initialSort };
  const [records, setRecords] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(initial);
  const [reloadKey, setReloadKey] = useState(0);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [selectionReset, setSelectionReset] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchPage({
      searchText: query.searchText.trim(),
      search: JSON.stringify(query.search),
      limit: query.size,
      skip: (query.page - 1) * query.size,
      sortBy: query.sort.key,
      sortOrder: query.sort.direction,
    })
      .then((res) => {
        if (!active) return;
        setRecords((res.data?.data || []).map((row, index) => ({ ...row, __index: index })));
        setTotalCount(res.data?.total_count || 0);
      })
      .catch((err) => active && showAlert({ type: "error", message: errorMessage(err, loadError) }))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // `fetchPage` is a stable api function; `loadError` is a constant label.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, reloadKey, showAlert]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);
  const resetSelection = useCallback(() => setSelectionReset((key) => key + 1), []);

  return {
    records,
    totalCount,
    loading,
    query,
    hasQuery: Boolean(query.searchText.trim()) || Object.keys(query.search).length > 0,
    start: (query.page - 1) * query.size,
    reload,
    // Changes on every reload / refresh, for data that should follow the list (e.g. stats above it).
    refreshKey: `${reloadKey}-${resetTrigger}`,
    resetTrigger,
    selectionReset,
    resetSelection,
    selectedRows,
    setSelectedRows,
    handleSearch: (searchText) => setQuery((current) => ({ ...current, searchText, page: 1 })),
    handleApplyFilters: ({ search }) => setQuery((current) => ({ ...current, search: search || {}, page: 1 })),
    handlePageChange: (page, newSize) => setQuery((current) => (newSize && newSize !== current.size ? { ...current, page: 1, size: newSize } : { ...current, page })),
    handleSort: (key) => setQuery((current) => ({ ...current, page: 1, sort: { key, direction: current.sort.key === key && current.sort.direction === "asc" ? "desc" : "asc" } })),
    handleRefresh: () => {
      setQuery({ ...initial });
      setResetTrigger((key) => key + 1);
      setSelectionReset((key) => key + 1);
    },
  };
}
